import { readFile, writeFile } from "node:fs/promises";
import { extname } from "node:path";
import { extractWikiLinks } from "./extract.js";
import { buildFileIndex } from "./resolve.js";

/**
 * Map of wiki-link names to replace.
 *
 * Both keys and values are the `name` portion of a wiki link — what
 * appears between `[[` and the optional `#anchor` / `|alias`. Anchors
 * and aliases on the original link are preserved automatically.
 *
 * Examples:
 *   { "API-Tokens": "secrets/api-tokens" }
 *     [[API-Tokens]]          → [[secrets/api-tokens]]
 *     [[API-Tokens|alias]]    → [[secrets/api-tokens|alias]]
 *     [[API-Tokens#A|alias]]  → [[secrets/api-tokens#A|alias]]
 *
 * Use the exact name string the operator wrote — no normalization is
 * applied. If the same broken target appears with different spellings
 * (e.g. case differences), provide one entry per spelling.
 */
export type RedirectionMap = ReadonlyMap<string, string>;

/**
 * Options for {@link rewriteDirectory}.
 */
export interface RewriteOptions {
  /**
   * The redirection map driving the rewrite. Links whose name is not
   * a key in this map are left untouched.
   */
  redirections: RedirectionMap;
  /**
   * When true, do not write any files. The result still describes the
   * rewrites that would happen — useful for showing the operator what
   * a redirection map will do before it does it.
   */
  dryRun?: boolean;
}

/**
 * Per-file detail of a rewrite operation.
 */
export interface FileRewrite {
  readonly sourcePath: string;
  readonly rewrites: readonly { readonly from: string; readonly to: string }[];
}

/**
 * Aggregate result of {@link rewriteDirectory}.
 */
export interface RewriteResult {
  readonly filesScanned: number;
  /** Files whose body was changed (or would be changed under dry-run). */
  readonly filesChanged: number;
  /** Total number of link replacements (may exceed `filesChanged`). */
  readonly rewritesApplied: number;
  /** Links matching a redirection key but ultimately not replaced — currently always 0; reserved for future skip reasons. */
  readonly skipped: number;
  readonly details: readonly FileRewrite[];
  readonly dryRun: boolean;
}

/**
 * Walk every `.md` file under `rootDir`, replace wiki links whose name
 * is a key in `redirections`, and write the changed files back to disk
 * (or describe what would change when `dryRun: true`).
 *
 * Only the link `name` is replaced. Anchors (`#Section`) and aliases
 * (`|display`) carry over verbatim onto the replacement link. The
 * surrounding markdown body is untouched.
 *
 * Rewriting is deterministic and order-independent — within a file,
 * every occurrence of every mapped key is replaced exactly once per
 * occurrence, with no chaining (the replacement is not re-scanned for
 * further matches).
 */
export async function rewriteDirectory(
  rootDir: string,
  opts: RewriteOptions,
): Promise<RewriteResult> {
  const { redirections, dryRun = false } = opts;
  const index = await buildFileIndex(rootDir);

  let filesScanned = 0;
  let filesChanged = 0;
  let rewritesApplied = 0;
  const details: FileRewrite[] = [];

  for (const paths of index.byBasename.values()) {
    for (const path of paths) {
      // Only markdown files have rewritable wiki-link bodies. Non-md
      // entries in the index (when callers asked for `additionalExtensions`
      // upstream) are resolution targets, not rewrite sources.
      if (extname(path) !== ".md") continue;
      filesScanned++;
      const raw = await readFile(path, "utf8");
      const { newBody, fileRewrites } = rewriteBody(raw, redirections);
      if (fileRewrites.length === 0) continue;
      filesChanged++;
      rewritesApplied += fileRewrites.length;
      details.push({ sourcePath: path, rewrites: fileRewrites });
      if (!dryRun) {
        await writeFile(path, newBody, "utf8");
      }
    }
  }

  return {
    filesScanned,
    filesChanged,
    rewritesApplied,
    skipped: 0,
    details,
    dryRun,
  };
}

/**
 * Pure transformation: take a markdown body, return the body with all
 * `[[…]]` links whose name appears in `redirections` rewritten. Exported
 * for testing and for callers that need to rewrite an in-memory string.
 *
 * The transformation does not touch markdown outside `[[…]]` patterns.
 */
export function rewriteBody(
  body: string,
  redirections: RedirectionMap,
): { newBody: string; fileRewrites: { from: string; to: string }[] } {
  const fileRewrites: { from: string; to: string }[] = [];
  // Reconstruct each match piece-by-piece so we keep the same anchor/
  // alias the operator wrote.
  const links = extractWikiLinks(body);
  if (links.length === 0) return { newBody: body, fileRewrites };

  let out = "";
  let cursor = 0;

  // We need positional info: walk the body with the same regex used in
  // extract.ts so we know where each match starts and ends.
  const pattern = /\[\[([^\]|#\n]+?)(?:#([^\]|\n]+?))?(?:\|([^\]\n]+?))?\]\]/g;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(body)) !== null) {
    const [whole, rawName, rawAnchor, rawAlias] = m;
    const name = (rawName ?? "").trim();
    const target = redirections.get(name);
    if (!target) continue;

    // Found a replacement. Emit everything up to this match unchanged…
    out += body.slice(cursor, m.index);
    // …then build the replacement preserving anchor and alias.
    let replacement = `[[${target}`;
    if (rawAnchor) replacement += `#${rawAnchor}`;
    if (rawAlias) replacement += `|${rawAlias}`;
    replacement += "]]";
    out += replacement;
    cursor = m.index + whole.length;
    fileRewrites.push({ from: whole, to: replacement });
  }

  // Tail: everything after the last replacement (or the whole body if
  // nothing matched).
  out += body.slice(cursor);
  return { newBody: out, fileRewrites };
}
